import { Room } from '@mui/icons-material'
import {
  Accordion,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { GeneralIcon, OptionsButton, RangeGraph, useMinimalsTheme } from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import {
  mapAllocationRuleDestinationToYardPositionDescriptor,
  mapAllocationRuleFacetsToString,
} from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { AllocationRuleDto } from '@yard/app/api'
import { RuleCapacity } from '@yard/utils/allocation-rule-capacity.util'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { AllocationRuleSelectedIndicator } from '../atoms/AllocationRuleSelectedIndicator'

const MAX_VISIBLE_CLASSES = 3

const ImoClassTagList: FC<{ imoClasses: string[] | null | undefined }> = ({ imoClasses }) => {
  const { t } = useTranslate()

  if (!imoClasses || imoClasses.length === 0) {
    return (
      <Stack direction='row' flexWrap='wrap' gap={0.5}>
        <Chip size='small' label={t('allDg', 'All DG')} variant='outlined' />
      </Stack>
    )
  }

  const sorted = [...imoClasses].sort((a, b) => a.localeCompare(b))
  const visible = sorted.slice(0, MAX_VISIBLE_CLASSES)
  const remaining = sorted.slice(MAX_VISIBLE_CLASSES)

  return (
    <Stack direction='row' flexWrap='wrap' gap={0.5}>
      {visible.map(code => (
        <Chip key={code} size='small' label={code} variant='outlined' />
      ))}
      {remaining.length > 0 && (
        <Tooltip title={remaining.join(', ')}>
          <Chip size='small' label={`+${remaining.length} more`} variant='outlined' />
        </Tooltip>
      )}
    </Stack>
  )
}

interface Props {
  allocationRule: AllocationRuleDto
  capacityByRule?: Record<string, RuleCapacity>
  matchingCountByRule?: Record<string, number>
  dragHandleProps?: any
  isEditable?: boolean
  isSelected?: boolean
  onMouseOver: () => void
  onMouseLeave: () => void
  onEdit: () => void
  onDelete: () => void
}
export const AssignedAllocationRule = observer(
  ({
    allocationRule,
    capacityByRule,
    matchingCountByRule,
    dragHandleProps,
    isEditable = true,
    isSelected = false,
    onMouseOver,
    onMouseLeave,
    onEdit,
    onDelete,
  }: Props) => {
    const { t } = useTranslate()
    const { palette, customSpacing } = useMinimalsTheme()
    const [expanded, setExpanded] = useState(false)
    const { yardBlockStore } = useYardStores()
    const capacity = capacityByRule?.[allocationRule.id]
    const matchingContainers = matchingCountByRule?.[allocationRule.id] ?? 0
    const isCapacityWarning = !!capacity && capacity.isWarning

    return (
      <Stack
        sx={{
          transition: 'none !important',
          borderLeft: `4px solid ${isSelected ? palette.primary.main : 'transparent'}`,
          '&:hover': {
            backgroundColor: !expanded ? palette.background.neutral : undefined,
          },
        }}
        direction='row'
        data-cy='allocation-rule'
      >
        <AllocationRuleSelectedIndicator
          selected={isSelected}
          dragHandleProps={dragHandleProps}
          showDragIndicator={isEditable}
        />

        <Stack
          onMouseLeave={onMouseLeave}
          onMouseOver={onMouseOver}
          flex={1}
          sx={{ userSelect: 'none' }}
        >
          <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            disableGutters
            sx={{
              '&.Mui-expanded': {
                boxShadow: 'none',
              },
            }}
          >
            <AccordionSummary
              sx={{
                pl: 1,
                '& .MuiAccordionSummary-content': {
                  my: '4px',
                  flexDirection: 'column',
                },
              }}
              onClick={e => e.stopPropagation()}
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  width: '100%',
                }}
              >
                <Stack sx={{ flexGrow: 1 }}>
                  <Typography
                    noWrap
                    variant='subtitle1'
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      maxWidth: '180px',
                    }}
                  >
                    {allocationRule.name}
                  </Typography>
                </Stack>

                {matchingContainers > 0 && (
                  <Typography
                    noWrap
                    variant='body1'
                    color={palette.text.secondary}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {matchingContainers}x <GeneralIcon />
                  </Typography>
                )}

                {capacity && (
                  <Tooltip
                    title={
                      capacity.isOvercapacity
                        ? t('allocationRuleOvercapacity', 'Over capacity')
                        : isCapacityWarning
                          ? t(
                              'allocationRuleCapacityWarning',
                              'Allocation rule approaching capacity',
                            )
                          : t('allocationRuleCapacityOk', 'Within capacity')
                    }
                  >
                    <Box
                      data-cy='allocation-rule-status-dot'
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: capacity.isOvercapacity
                          ? palette.error.main
                          : isCapacityWarning
                            ? palette.warning.main
                            : palette.success.main,
                        ml: 1,
                        mr: isEditable ? 0 : '40px',
                      }}
                    />
                  </Tooltip>
                )}

                {isEditable && (
                  <OptionsButton
                    options={[
                      {
                        label: t('edit', 'Edit'),
                        onClick: onEdit,
                      },
                      {
                        label: t('delete', 'Delete'),
                        onClick: onDelete,
                      },
                    ]}
                  />
                )}
              </Stack>

              {capacity && capacity.totalTeu > 0 && (
                <Stack sx={{ width: '100%' }} gap={0.25}>
                  <Tooltip
                    title={t(
                      'allocationRuleCapacityBreakdown',
                      'Occupied / Reserved / Planned / Free TEU',
                    )}
                  >
                    <span>
                      <RangeGraph
                        height={6}
                        hideLabels
                        options={[
                          {
                            value: capacity.occupiedTeu,
                            color: palette.primary.main,
                            textColor: 'white',
                            order: 0,
                          },
                          {
                            value: capacity.reservedTeu,
                            color: palette.warning.main,
                            textColor: 'white',
                            order: 1,
                          },
                          {
                            value: capacity.plannedTeu,
                            color: palette.info.main,
                            textColor: 'white',
                            order: 2,
                          },
                          {
                            value: capacity.freeTeu,
                            color: palette.grey[400],
                            textColor: 'white',
                            order: 3,
                          },
                        ]}
                      />
                      <Typography variant='caption' color={palette.text.secondary}>
                        {t('allocationRuleAvailable', '{pct}% — {count} available', {
                          pct: capacity.utilizationPct,
                          count: capacity.available,
                        })}
                        {' · '}
                        {capacity.occupiedTeu}/{capacity.reservedTeu}/{capacity.plannedTeu}/
                        {capacity.freeTeu} TEU
                      </Typography>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </AccordionSummary>

            <Stack p={1} pt={0} sx={{ gap: 1 }}>
              <Stack
                direction='row'
                alignItems='center'
                gap={customSpacing.xxs}
                sx={{ width: '100%' }}
              >
                <Typography
                  color={palette.text.secondary}
                  variant='body2'
                  display='flex'
                  alignItems='center'
                >
                  <GeneralIcon />
                </Typography>
                <Typography
                  color={palette.text.secondary}
                  sx={{ maxWidth: '310px' }}
                  variant='body2'
                >
                  {mapAllocationRuleFacetsToString(
                    allocationRule.facets,
                    t,
                    allocationRule.enforcementMode,
                  )}
                </Typography>
                {allocationRule.facets.isDangerous === true &&
                  allocationRule.enforcementMode && (
                    <Chip
                      size='small'
                      label={allocationRule.enforcementMode}
                      sx={{
                        bgcolor:
                          allocationRule.enforcementMode === 'Block'
                            ? palette.warning.main
                            : palette.info.main,
                        color: 'white',
                        ml: 0.5,
                      }}
                    />
                  )}
              </Stack>

              {allocationRule.facets.isDangerous === true && (
                <ImoClassTagList imoClasses={allocationRule.facets.imoClasses ?? null} />
              )}

              <Divider sx={{ mt: 0 }} />

              <Stack gap={1} alignItems='center' sx={{ color: palette.grey[600] }}>
                {allocationRule.destinations.map((destination, index) => (
                  <Stack
                    key={index}
                    direction='row'
                    alignItems='center'
                    spacing={0.5}
                    sx={{ width: '100%' }}
                  >
                    <Room fontSize='small' />
                    <Typography>
                      {mapAllocationRuleDestinationToYardPositionDescriptor(
                        yardBlockStore.yardBlocks[destination.blockId],
                        destination.minBay,
                        destination.maxBay,
                        destination.minRow,
                        destination.maxRow,
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Accordion>
        </Stack>
      </Stack>
    )
  },
)
